import {
  AppCard,
  Button,
  Icon,
  PageContainer,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiPencil } from "@mdi/js";
import { Box, CircularProgress, Typography } from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ValorationRequestDetailsType } from "../../../../../@types/services/ValorationRequest";
import { MmHistoryLogs } from "../../../../../components/molecules/MmHistoryLogs";
import { MmRequestAttachments } from "../../../../../components/molecules/MmRequestAttachments";
import { MmRequestHeader } from "../../../../../components/molecules/MmRequestHeader";
import { MmRequestComments } from "../../../../../components/molecules/MmRequestsComments";
import {
  MmRequestTabs,
  type MmRequestTabValue,
} from "../../../../../components/molecules/MmRequestTabs";
import { ValorationRequestDetails } from "../../../../../components/molecules/ValorationRequestForm/details";
import { useAuth } from "../../../../../hooks/useAuth";
import { fetchValorationRequest } from "../../../../../services/valorationRequests";
import { mmRequestTabsSearchSchema } from "../../mm_requests/-schemas/mmRequestTabsSearchSchema";

const ShowValorationRequest: React.FC = () => {
  const { t } = useTranslation();
  const navigate = Route.useNavigate();
  const { user } = useAuth();

  const { valorationRequestId } = Route.useParams();
  const { tab } = Route.useSearch();

  const [isFetchingData, setIsFetchingData] = useState<boolean>(true);
  const [mmRequestId, setMmRequestId] = useState<number | null>(null);
  const [requestDetailsData, setRequestDetailsData] =
    useState<ValorationRequestDetailsType>();

  useEffect(() => {
    if (!valorationRequestId) return;

    fetchValorationRequest(valorationRequestId)
      .then((response) => {
        setMmRequestId(response.mm_request.id);

        setRequestDetailsData({
          id: parseInt(valorationRequestId, 10),
          mm_request: {
            assigned_to: response.mm_request.assigned_to,
            backup_requester_id: response.mm_request.backup_requester?.id,
            completed: response.mm_request.process_instance.completed,
            created_at: response.mm_request.created_at,
            deadline: response.mm_request.deadline,
            description: response.mm_request.description,
            id: response.mm_request.id,
            matnumber: response.mm_request.matnumber,
            previous_ticket_id: response.mm_request.previous_ticket_id,
            quality_deviation: response.mm_request.quality_deviation,
            quality_deviation_description:
              response.mm_request.quality_deviation_description,
            requester_area: response.mm_request.requester_area,
            requester_field: response.mm_request.requester_field,
            subject: response.mm_request.subject,
            threat: response.mm_request.threat,
            updated_at: response.mm_request.updated_at,
          },
          plant_ids: response.plant_ids,
        });
      })
      .catch(() => {
        enqueueSnackbar(t("mmRequests.fetchError"), {
          variant: "error",
        });
      })
      .finally(() => {
        setIsFetchingData(false);
      });
  }, [valorationRequestId, t]);

  const handleTabChange = useCallback(
    (newValue: MmRequestTabValue): void => {
      navigate({
        replace: true,
        resetScroll: false,
        search: (prev) => ({
          ...prev,
          tab: newValue,
        }),
      });
    },
    [navigate],
  );

  const handleEdit = useCallback((): void => {
    if (!valorationRequestId) return;

    navigate({
      params: { valorationRequestId },
      to: "/vbuyer/valoration_requests/$valorationRequestId/edit",
    });
  }, [navigate, valorationRequestId]);

  const userCanEdit = useMemo(() => {
    const mmRequest = requestDetailsData?.mm_request;

    if (!user || !mmRequest) return false;

    return (
      (user.roles_names.includes("Edit material requests") ||
        user.id === mmRequest.assigned_to?.id) &&
      !mmRequest.completed
    );
  }, [requestDetailsData?.mm_request, user]);

  const title = mmRequestId
    ? `${t("common.valorationRequest")} #${mmRequestId}`
    : t("common.valorationRequest");

  if (isFetchingData) {
    return (
      <PageContainer showBackButton title={title}>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  const isCompleted = requestDetailsData?.mm_request.completed ?? false;

  return (
    <PageContainer showBackButton title={title}>
      {!!mmRequestId && <MmRequestHeader id={mmRequestId} />}

      <AppCard>
        <MmRequestTabs onChange={handleTabChange} value={tab} />
        <Box sx={{ marginTop: "32px" }}>
          {tab === "details" && requestDetailsData && (
            <AppCard
              header={
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="h6">{t("details")}</Typography>
                  {userCanEdit && (
                    <Button
                      onClick={handleEdit}
                      startIcon={<Icon icon={mdiPencil} />}
                      variant="outlined"
                    >
                      {t("common.edit")}
                    </Button>
                  )}
                </Box>
              }
            >
              <ValorationRequestDetails
                requestDetailsData={requestDetailsData}
              />
            </AppCard>
          )}
          {tab === "attachments" && mmRequestId && (
            <MmRequestAttachments
              isCompleted={isCompleted}
              mmRequestId={mmRequestId}
            />
          )}
          {tab === "comments" && mmRequestId && (
            <MmRequestComments
              isCompleted={isCompleted}
              mmRequestId={mmRequestId}
            />
          )}
          {tab === "history" && mmRequestId && (
            <MmHistoryLogs mmRequestId={mmRequestId} />
          )}
        </Box>
      </AppCard>
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/vbuyer/valoration_requests/$valorationRequestId/",
)({
  component: ShowValorationRequest,
  validateSearch: mmRequestTabsSearchSchema,
});
